import { Events, pubsub } from '@/javascript/service/pubsub';
import logger from '@service/logger';

class DatabaseStoreConfig {
  /**
   * @type {string}
   */
  name;

  /**
   * @type {string}
   */
  keyPath;
}

class DatabaseManager {
  /**
   * @type {string}
   */
  dbName;
  /**
   * @type {number}
   */
  version;

  /**
   * @type {IDBDatabase}
   */
  _db;

  constructor(dbName, version = 1) {
    this.dbName = dbName;
    this.version = version;
    this._db = null;
  }

  /**
   * Upgrade the database to the latest version.
   * @param {DatabaseStoreConfig[]} [storeConfigurations=[]] - Array of store configurations
   * @returns {Promise<IDBDatabase>}
   */
  async upgradeDatabase(storeConfigurations = []) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onupgradeneeded = event => {
        const db = event.target.result;
        storeConfigurations.forEach(storeConfiguration => {
          const storeName = storeConfiguration.name;
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: storeConfiguration.keyPath });
            pubsub.pub(`database:${storeName}:created`, `Created store ${storeName}`);
          }
        });
        this._db = db;
        resolve(this._db);
      };
      request.onsuccess = event => {
        this._db = event.target.result;
        resolve(this._db);
      };
      request.onerror = event => {
        reject(event.target.error);
      };
      request.onblocked = event => {
        reject(new Error('Database upgrade blocked'));
      };
    });
  }

  /**
   *
   * @param {DatabaseStoreConfig[]} [storeConfigurations=[]]
   * @returns {Promise<void>}
   */
  async init(storeConfigurations = []) {
    return new Promise((resolve, reject) => {
      this.upgradeDatabase(storeConfigurations)
        .then(upgradeResult => {
          resolve();
        })
        .catch(reject);
    });
  }

  getSimpleDb(storeName) {
    return new SimpleDb(this.dbName, storeName);
  }

  namedStore(storeName) {
    return this.getSimpleDb(storeName);
  }
}

export class SimpleDb {
  constructor(dbName, storeName, version = 1) {
    this.dbName = dbName;
    this.storeName = storeName;
    this.version = version;
    this.db = null;
  }

  /**
   * Gets the store in read-only mode or read-write mode. By default it is read-only.
   *
   * @param {IDBTransactionMode} [mode='readonly']
   * @returns {Promise<IDBObjectStore>}
   */
  async getStore(mode = 'readonly') {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.storeName], mode);
        const objectStore = transaction.objectStore(this.storeName);

        transaction.oncomplete = () => {
          // Transaction completed successfully
        };

        transaction.onerror = event => {
          reject(event.target.error);
        };

        transaction.onabort = event => {
          reject(new Error('Transaction aborted'));
        };

        // Return the object store immediately
        resolve(objectStore);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   *
   * @param {IDBKeyRange|IDBValidKey} key
   * @returns {Promise<unknown>}
   */
  async get(key) {
    const store = await this.getStore();
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = event => {
        resolve(event.target.result);
      };
      request.onerror = event => {
        reject(event.target.error);
      };
    });
  }

  /**
   * Put a value into the database
   * @param {unknown} value
   * @returns {Promise<void>}
   */
  async put(value) {
    const store = await this.writable();

    return new Promise((resolve, reject) => {
      const request = store.put(value);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = event => {
        reject(event.target.error);
      };
    });
  }

  /**
   * Gets a readable version of the store.
   *
   * @returns {Promise<IDBObjectStore>}
   */
  async readable() {
    return this.getStore('readonly');
  }

  /**
   * Gets a writable version of the store.
   *
   * @returns {Promise<unknown>}
   */
  async writable() {
    return this.getStore('readwrite');
  }

  /**
   *
   * @returns {Promise<boolean>}
   */
  async isEmpty() {
    const store = await this.readable();
    const request = store.count();
    return new Promise((resolve, reject) => {
      request.onsuccess = event => {
        const maybe = event.target.result === 0;
        resolve(maybe);
      };
      request.onerror = event => {
        reject(event.target.error);
      };
    });
  }

  async init(storeConfig = {}) {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onupgradeneeded = event => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, storeConfig);
        }
      };
      request.onsuccess = event => {
        this.db = event.target.result;
        resolve(this.db);
      };
      request.onerror = event => {
        reject(event.target.error);
      };
      request.onblocked = event => {
        reject(new Error('Database upgrade blocked'));
      };
    });
  }
}

const databaseName = 'bcs_db';
const database = new DatabaseManager(databaseName, 1);
export default database;
